import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Users, Plus, Trash2, Lock, Globe, Share2, Copy, Edit2, RefreshCw } from 'lucide-react';
import { GlassButton } from '../components/ui/GlassButton';
import { AddGroupModal } from '../components/AddGroupModal';
import { EditGroupModal } from '../components/EditGroupModal';
import { Toast } from '../Toast';
import { ConfirmModal } from '../ConfirmModal';

import { backendOriginForAssets } from '../config/env';

// Base URL para fotos de grupo (backend serve /group_photos)
const groupPhotosBase = backendOriginForAssets;

interface GroupData {
    id: string;
    title: string;
    description?: string;
    invite_link?: string;
    username?: string;
    is_public: boolean;
    chat_id: string;
    photo_url?: string;
}

export const Groups = ({ accountId }: { accountId: number | string | null }) => {
    const [groups, setGroups] = useState<GroupData[]>([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingGroup, setEditingGroup] = useState<GroupData | null>(null);
    const [toast, setToast] = useState<{ msg: string, type: 'success' | 'error' } | null>(null);
    const [groupToDelete, setGroupToDelete] = useState<GroupData | null>(null);
    const [syncing, setSyncing] = useState(false);


    useEffect(() => {
        if (accountId) {
            fetchGroups(); // Load instant from DB

            // Auto Refresh every 60 seconds
            const interval = setInterval(() => {
                fetchGroups();
            }, 60000);

            return () => clearInterval(interval);
        } else {
            setGroups([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountId]);

    const handleSync = async () => {
        if (!accountId) return;
        setSyncing(true);
        try {
            await api.post('/groups/sync', { account_id: accountId });
            setToast({ msg: "Grupos sincronizados com sucesso!", type: 'success' });
            fetchGroups();
        } catch (e) {
            console.error(e);
            setToast({ msg: "Erro ao sincronizar grupos.", type: 'error' });
        } finally {
            setSyncing(false);
        }
    };

    const fetchGroups = async () => {
        if (!accountId) return;
        setLoading(true);
        try {
            const res = await api.get('/groups', { params: { account_id: accountId } });
            const list = Array.isArray(res.data) ? res.data : [];
            setGroups(list);
        } catch (e) {
            console.error("Erro fetch groups", e);
            setGroups([]);
            setToast({ msg: "Erro ao carregar grupos.", type: 'error' });
        } finally {
            setLoading(false);
        }
    };


    const handleDelete = async () => {
        if (!groupToDelete) return;
        try {
            await api.delete(`/groups/${groupToDelete.id}`);
            setGroups(groups.filter(g => g.id !== groupToDelete.id));
            setToast({ msg: "Grupo excluído.", type: 'success' });
        } catch (e) {
            setToast({ msg: "Erro ao excluir grupo.", type: 'error' });
        } finally {
            setGroupToDelete(null);
        }
    };

    const copyLink = (link?: string) => {
        if (!link) return;
        navigator.clipboard.writeText(link);
        setToast({ msg: "Link copiado!", type: 'success' });
    };


    return (
        <div className="dashboard-content" style={{ padding: '20px' }}>
            {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

            {showModal && (
                <AddGroupModal
                    accountId={accountId}
                    onClose={() => setShowModal(false)}
                    onSuccess={() => {
                        setToast({ msg: "Grupo criado com sucesso!", type: 'success' });
                        fetchGroups();
                    }}
                />
            )}

            {editingGroup && (
                <EditGroupModal
                    group={editingGroup}
                    onClose={() => setEditingGroup(null)}
                    onSuccess={() => {
                        setToast({ msg: "Grupo atualizado com sucesso!", type: 'success' });
                        fetchGroups();
                    }}
                />
            )}

            {groupToDelete && (
                <ConfirmModal
                    isOpen={!!groupToDelete}
                    title="Excluir Grupo"
                    message={`Tem certeza que deseja apagar o grupo "${groupToDelete.title}"? Isso excluirá o grupo no Telegram também!`}
                    onConfirm={handleDelete}
                    onCancel={() => setGroupToDelete(null)}
                />
            )}

            <div className="top-bar">
                <h1 className="page-title">Gerenciar Grupos</h1>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <GlassButton onClick={handleSync} disabled={syncing || !accountId} style={{ width: 'auto', background: 'rgba(255,255,255,0.05)' }}>
                        <RefreshCw size={18} className={syncing ? 'spin-animation' : ''} /> {syncing ? 'Sincronizando...' : 'Sincronizar'}
                    </GlassButton>
                    <GlassButton onClick={() => setShowModal(true)} style={{ width: 'auto' }}>
                        <Plus size={18} /> Novo Grupo
                    </GlassButton>
                </div>
            </div>

            {loading && groups.length === 0 ? (
                null
            ) : groups.length === 0 ? (
                <div className="empty-state-pro">
                    <div className="empty-icon-box">
                        <Users size={48} />
                    </div>
                    <h3>Nenhum grupo encontrado</h3>
                    <p>Sua lista de grupos é atualizada automaticamente.</p>
                </div>
            ) : (
                <div className="groups-grid" style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                    gap: '20px',
                    marginTop: '20px'
                }}>
                    {groups.map(group => (
                        <div key={group.id} style={{
                            background: '#161618',
                            borderRadius: '16px',
                            border: '1px solid #333',
                            padding: '24px',
                            display: 'flex', flexDirection: 'column', gap: '16px',
                            position: 'relative'
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                    <div style={{
                                        width: '48px', height: '48px', borderRadius: '12px',
                                        background: group.photo_url ? 'transparent' : 'rgba(255, 106, 0, 0.1)',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        overflow: 'hidden',
                                        border: group.photo_url ? '2px solid #333' : 'none'
                                    }}>
                                        {group.photo_url ? (
                                            <img
                                                src={group.photo_url ? `${groupPhotosBase}${group.photo_url}` : ''}
                                                alt={group.title}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                onError={(e) => {
                                                    // Fallback para ícone se imagem falhar
                                                    e.currentTarget.style.display = 'none';
                                                    e.currentTarget.parentElement!.style.background = 'rgba(255, 106, 0, 0.1)';
                                                    const icon = document.createElement('div');
                                                    icon.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#ff6a00" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>';
                                                    e.currentTarget.parentElement!.appendChild(icon);
                                                }}
                                            />
                                        ) : (
                                            <Users size={20} color="#ff6a00" />
                                        )}
                                    </div>
                                    <div>
                                        <h3 style={{ margin: 0, fontSize: '16px', color: '#fff' }}>{group.title}</h3>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px', flexWrap: 'wrap' }}>
                                            {group.is_public ? <Globe size={12} color="#ff6a00" /> : <Lock size={12} color="#f59e0b" />}
                                            <span style={{ fontSize: '12px', color: '#888' }}>{group.is_public ? 'Público' : 'Privado'}</span>
                                            <span
                                                onClick={() => {
                                                    navigator.clipboard.writeText(group.chat_id.toString());
                                                    setToast({ msg: "ID Copiado!", type: 'success' });
                                                }}
                                                style={{
                                                    fontSize: '11px', color: '#555', marginLeft: '6px',
                                                    fontFamily: 'monospace', background: '#222', padding: '2px 6px',
                                                    borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px'
                                                }}
                                                title="Clique para copiar ID"
                                            >
                                                ID: {group.chat_id} <Copy size={10} />
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div style={{
                                background: '#0a0a0a', padding: '12px', borderRadius: '10px',
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                border: '1px solid #222'
                            }}>
                                <span style={{
                                    color: '#ff6a00', fontSize: '13px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px', cursor: 'pointer'
                                }} onClick={() => copyLink(group.invite_link)}>
                                    {group.invite_link || group.username ? (group.invite_link || `t.me/${group.username}`) : 'Sem link'}
                                </span>
                                <button onClick={() => copyLink(group.invite_link)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#666' }}>
                                    <Copy size={16} />
                                </button>
                            </div>

                            <div style={{ display: 'flex', gap: '10px', marginTop: 'auto', paddingTop: '16px', borderTop: '1px solid #222' }}>
                                {group.invite_link && (
                                    <a
                                        href={group.invite_link.startsWith('http') ? group.invite_link : `https://${group.invite_link}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{
                                            flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #333',
                                            background: '#1a1a1c', color: '#eee', textAlign: 'center', textDecoration: 'none', fontSize: '13px',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                                        }}
                                    >
                                        <Share2 size={14} /> Abrir
                                    </a>
                                )}
                                <button
                                    onClick={() => setEditingGroup(group)}
                                    style={{
                                        padding: '10px', borderRadius: '8px', border: '1px solid #333',
                                        background: '#222', color: '#eee', cursor: 'pointer'
                                    }}
                                    title="Editar Grupo"
                                >
                                    <Edit2 size={16} />
                                </button>
                                <button
                                    onClick={() => setGroupToDelete(group)}
                                    style={{
                                        padding: '10px', borderRadius: '8px', border: '1px solid #ef4444',
                                        background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', cursor: 'pointer'
                                    }}
                                    title="Excluir Grupo"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
